import { memo, useMemo, useState, useEffect, useRef } from 'react';
import { arc as d3Arc } from 'd3-shape';
import { scaleBand, scaleRadial } from 'd3-scale';
import { max } from 'd3-array';
import { DELIVERY_TYPE_COLORS } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { usePartnershipData } from './usePartnershipData.jsx';

function useAnimatedProgress(key, duration = 500) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    setProgress(0);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    function step(now) {
      const t = Math.min((now - start) / duration, 1);
      setProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [key, duration]);

  return progress;
}

const SIZE = 600;
const CX = SIZE / 2;
const CY = SIZE / 2;
const INNER_R = 80;
const OUTER_R = 240;
const LABEL_R = OUTER_R + 14;

export const PartnerRadialChart = memo(function PartnerRadialChart() {
  const { networkData, localFilteredResults } = usePartnershipData();
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  const partnerNodes = networkData?.partnerNodes;

  // Chart data: same partners as chord chart, sorted by totalResults
  const chartData = useMemo(() => {
    if (!partnerNodes || partnerNodes.length === 0) return [];
    return partnerNodes
      .map(p => ({
        id: p.acronym,
        label: p.acronym.length > 12 ? p.acronym.substring(0, 11) + '\u2026' : p.acronym,
        fullName: p.fullName,
        total: p.totalResults,
        primaryType: p.deliveryTypes?.[0] || 'Other',
        color: DELIVERY_TYPE_COLORS[p.deliveryTypes?.[0]] || DELIVERY_TYPE_COLORS.Other,
      }))
      .sort((a, b) => b.total - a.total);
  }, [partnerNodes]);

  // Animation: grow bars from center on data change
  const dataKey = useMemo(() =>
    chartData.map(d => d.id + d.total).join(','),
    [chartData]
  );
  const progress = useAnimatedProgress(dataKey);

  // Export data
  const exportData = useMemo(() =>
    chartData.map(d => ({ partner: d.id, full_name: d.fullName, delivery_type: d.primaryType, result_count: d.total })),
    [chartData]
  );

  // D3 layout
  const layout = useMemo(() => {
    if (chartData.length === 0) return null;

    const x = scaleBand()
      .domain(chartData.map(d => d.id))
      .range([0, 2 * Math.PI])
      .padding(0.1);

    const maxVal = max(chartData, d => d.total) || 1;
    const y = scaleRadial()
      .domain([0, maxVal])
      .range([INNER_R, OUTER_R]);

    const ticks = y.ticks(4).filter(t => t > 0);

    return { x, y, ticks };
  }, [chartData]);

  if (!layout || chartData.length === 0) return null;

  const { x, y, ticks } = layout;

  const arcGen = d3Arc()
    .innerRadius(INNER_R)
    .outerRadius(d => INNER_R + (y(d.total) - INNER_R) * progress)
    .startAngle(d => x(d.id))
    .endAngle(d => x(d.id) + x.bandwidth())
    .padAngle(0.01)
    .padRadius(INNER_R);

  // Delivery type legend — only types present
  const legendTypes = [...new Set(chartData.map(d => d.primaryType))].sort();

  return (
    <ChartWrapper title="Partner Network (Radial)" info="Radial bar chart showing the same partners as the chord diagram, sorted by result count. Bar color indicates the partner's primary delivery type." chartData={exportData} sourceResults={localFilteredResults} exportFilename="partner-network-radial">
      <div className="flex justify-center">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full"
          style={{ maxWidth: SIZE, height: 'auto' }}
        >
          <g transform={`translate(${CX},${CY})`}>
            {/* Y-axis grid circles */}
            {ticks.map(t => (
              <g key={t}>
                <circle r={y(t)} fill="none" stroke="#9ca3af" strokeDasharray="2,3" />
                <text y={-y(t)} dy="-3" textAnchor="middle" fontSize={9} fill="#4b5563">
                  {t}
                </text>
              </g>
            ))}

            {/* Inner circle */}
            <circle r={INNER_R} fill="none" stroke="#6b7280" />

            {/* Bars */}
            {chartData.map(d => (
              <path
                key={d.id}
                d={arcGen(d)}
                fill={d.color}
                opacity={0.85}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  setTip({ name: d.fullName || d.id, acronym: d.id, total: d.total, type: d.primaryType, color: d.color });
                  setTipPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTip(null)}
              />
            ))}

            {/* Partner labels */}
            {chartData.map(d => {
              const midAngle = x(d.id) + x.bandwidth() / 2;
              const lx = LABEL_R * Math.sin(midAngle);
              const ly = -LABEL_R * Math.cos(midAngle);
              const rotation = (midAngle * 180) / Math.PI;
              const flip = midAngle > Math.PI;
              return (
                <text
                  key={d.id}
                  x={lx}
                  y={ly}
                  dy="0.35em"
                  textAnchor={flip ? 'end' : 'start'}
                  transform={`rotate(${rotation - 90},${lx},${ly})${flip ? ` rotate(180,${lx},${ly})` : ''}`}
                  fontSize={12}
                  fontWeight={500}
                  fill="#374151"
                  opacity={progress}
                  style={{ cursor: 'default' }}
                  onMouseEnter={(e) => {
                    setTip({ name: d.fullName || d.id, acronym: d.id, total: d.total, type: d.primaryType, color: d.color });
                    setTipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTip(null)}
                >
                  {d.label}
                </text>
              );
            })}
          </g>
        </svg>
        {tip && (
          <div className="fixed z-50 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg border border-gray-200 pointer-events-none"
               style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
            <div className="font-semibold mb-1">{tip.name}</div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: tip.color }} />
              Results: {tip.total}
            </div>
            <div className="text-gray-500 mt-1 border-t border-gray-200 pt-1">
              {tip.type}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
        <span className="font-medium text-gray-700">Delivery type:</span>
        {legendTypes.map(k => (
          <div key={k} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DELIVERY_TYPE_COLORS[k] || DELIVERY_TYPE_COLORS.Other }} />
            <span>{k}</span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});
